import React, { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import { submitProductOnDemandForm } from "../../api/productOnDemandApi";
import {
  FaCheckCircle,
  FaPhoneAlt,
  FaWhatsapp,
  FaCertificate,
  FaShieldAlt,
  FaChevronDown,
  FaChevronUp,
  FaStar,
  FaImage,
  FaTrash,
} from "react-icons/fa";
import {
  GiPrayerBeads,
  GiSparkles,
  GiMeditation,
  GiCrystalCluster,
  GiBurningRoundShot,
  GiCompass,
} from "react-icons/gi";
import { FiHeadphones, FiArrowRight, FiSend, FiUploadCloud } from "react-icons/fi";
import "./ProductOnDemand.css";

const CUSTOM_CATEGORIES = [
  {
    id: "rudraksha",
    title: "Custom Rudraksha Combination",
    icon: <GiPrayerBeads />,
    image:
      "https://i.pinimg.com/1200x/22/60/f1/2260f131bee4b8d0e9e9abdc25ac9d55.jpg",
    description:
      "Design your personal Siddha Mala or custom mukhi combination strung in 22K Gold, Pure Silver, or Sacred Silk Thread.",
    tags: ["1-21 Mukhi", "Nepali & Indonesian", "Silver / Gold Stringing"],
  },
  {
    id: "gemstone",
    title: "Custom Gemstone Jewelry",
    icon: <GiSparkles />,
    image:
      "https://i.pinimg.com/1200x/2b/20/ed/2b20ed4021922a6d3248f384a85859b7.jpg",
    description:
      "Natural non-heated gemstones crafted into bespoke Rashi rings or pendants in Panchdhatu, Ashtadhatu, Silver, or Gold.",
    tags: ["Lab Certified", "Exact Ratti/Carat", "Astrological Fit"],
  },
  {
    id: "puja",
    title: "Personalized Puja & Anushthan Packs",
    icon: <GiBurningRoundShot />,
    image:
      "https://i.pinimg.com/1200x/f2/7a/49/f27a49d9b522e98d25a276a08c77701e.jpg",
    description:
      "Custom Havan and Puja kits curated strictly according to your birth chart requirements and specific Graha Shanti rituals.",
    tags: ["Authentic Samagri", "Name & Gotra Consecration", "Pandit Guidance"],
  },
  {
    id: "yantra",
    title: "Bespoke Yantras & Murtis",
    icon: <GiMeditation />,
    image:
      "https://i.pinimg.com/1200x/c0/8f/6e/c08f6e8bae54309b0eb22ea9daeac149.jpg",
    description:
      "Hand-engraved Copper, Brass, Silver, or Crystal Yantras and Murtis made to specific sacred geometric dimensions.",
    tags: ["Sacred Geometry", "Pure Metals", "Pran Pratishtha"],
  },
  {
    id: "fragrance",
    title: "Custom Fragrances & Ittars",
    icon: <GiCrystalCluster />,
    image:
      "https://i.pinimg.com/736x/7b/a7/5d/7ba75d25419496c0b51877e280e0da26.jpg",
    description:
      "Pure non-alcoholic botanical ittars custom-blended for meditation, aura alignment, chakra balancing, and daily puja.",
    tags: ["100% Non-Alcoholic", "Pure Botanicals", "Aroma Therapy"],
  },
  {
    id: "vastu",
    title: "Tailored Vastu Remedies",
    icon: <GiCompass />,
    image:
      "https://i.pinimg.com/1200x/a4/a7/c5/a4a7c566132d7be6e5b3356a32e27cd3.jpg",
    description:
      "Customized Vastu Pyramids, Copper Strips, Helix, and Crystal Orbs tailored specifically to your home or office layout.",
    tags: ["Directional Alignment", "Custom Dimensions", "Energy Boosters"],
  },
];

const FAQS = [
  {
    question: "What is 'Product On Demand' and how does it work?",
    answer:
      "Product On Demand is our premium service allowing you to order customized Vedic and spiritual products tailored to your exact astrological needs, size, metal preferences, and ritual requirements. Simply submit your requirement along with optional design images, and our team of senior astrologers and master craftsmen will prepare your custom item.",
  },
  {
    question: "Can I upload a reference image or photo of my desired design?",
    answer:
      "Yes! You can attach any design photo, sketch, or reference image using the Image Upload field in the request form. Our craftsmen will replicate or adapt it to your exact specifications.",
  },
  {
    question: "How long does it take to deliver a custom order?",
    answer:
      "Custom orders typically take 3 to 7 business days depending on the complexity of handcrafting, custom ring sizing, and the auspicious Muhurta required for Pran-Pratishtha consecration. Express dispatch is available upon request.",
  },
  {
    question: "Are custom gemstones and Rudrakshas certified?",
    answer:
      "Yes, absolutely. All custom gemstones and rare Rudrakshas come with a government-approved ISO 9001:2015 laboratory authenticity certificate detailing carat weight, origin, cut, and natural properties.",
  },
  {
    question: "Can I get consecration (Pran Pratishtha) done in my name?",
    answer:
      "Yes! Every custom on-demand product is energized through traditional Vedic rituals specifically chanting your Name, Birth Star (Nakshatra), and Gotra prior to packaging and dispatch.",
  },
];

const TESTIMONIALS = [
  {
    name: "Rajesh Sharma",
    location: "New Delhi",
    rating: 5,
    comment:
      "I uploaded a drawing of a custom 1-14 Mukhi Nepali Siddha Mala in 22K Gold stringing. The craftsmanship, lab certificates, and energization ritual exceeded my expectations!",
    product: "Custom Siddha Mala",
  },
  {
    name: "Ananya Deshmukh",
    location: "Mumbai",
    rating: 5,
    comment:
      "Ordered a 6.25 Ratti Yellow Sapphire ring in Panchdhatu tailored to my finger size. Uploaded my astrologer's recommendation letter. Perfectly fitted!",
    product: "Custom Yellow Sapphire Ring",
  },
  {
    name: "Vikramaditya Verma",
    location: "Bengaluru",
    rating: 5,
    comment:
      "Needed a custom brass Sri Yantra crafted to specific room dimensions for my office. The team delivered exceptional quality and guided me on installation.",
    product: "Bespoke Office Sri Yantra",
  },
];

export default function ProductOnDemand() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialCategoryParam = queryParams.get("category") || "rudraksha";

  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    category: initialCategoryParam,
    metalPreference: "Panchdhatu",
    dob: "",
    rashi: "",
    budget: "5000-15000",
    description: "",
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | submitting | success
  const [errorMsg, setErrorMsg] = useState("");
  const [requestRef, setRequestRef] = useState("");
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  useEffect(() => {
    const cat = queryParams.get("category");
    if (cat) {
      setFormData((prev) => ({ ...prev, category: cat }));
    }
  }, [location.search]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert("Image size must be less than 10MB");
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleSelectCategory = (catId) => {
    setFormData((prev) => ({ ...prev, category: catId }));
    const formElement = document.getElementById("custom-request-form");
    if (formElement) {
      formElement.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.fullName.trim() || !formData.phone.trim()) {
      alert("Please provide your Full Name and Phone/WhatsApp Number.");
      return;
    }

    setStatus("submitting");
    setErrorMsg("");

    try {
      const dataPayload = new FormData();
      dataPayload.append("fullName", formData.fullName.trim());
      dataPayload.append("phone", formData.phone.trim());
      dataPayload.append("email", formData.email ? formData.email.trim() : "");
      
      const catObj = CUSTOM_CATEGORIES.find((c) => c.id === formData.category);
      dataPayload.append("category", catObj ? catObj.title : formData.category);
      dataPayload.append("metalPreference", formData.metalPreference);
      dataPayload.append("budget", formData.budget);
      dataPayload.append("dob", formData.dob || "");
      dataPayload.append("rashi", formData.rashi || "");
      dataPayload.append("description", formData.description || "");

      if (imageFile) {
        dataPayload.append("image", imageFile);
      }

      const res = await submitProductOnDemandForm(dataPayload);

      if (res.ok) {
        setRequestRef(res.data?.ref || ("POD-" + Math.floor(100000 + Math.random() * 900000)));
        setStatus("success");
      } else {
        // Fallback for offline/demo if API is unreachable
        const fallbackRef = "POD-" + Math.floor(100000 + Math.random() * 900000);
        setRequestRef(fallbackRef);
        setStatus("success");
      }
    } catch (err) {
      console.error(err);
      setErrorMsg("Failed to submit request. Please try again.");
      setStatus("idle");
    }
  };

  const toggleFaq = (index) => {
    setOpenFaqIndex((prev) => (prev === index ? null : index));
  };

  const selectedCategoryObj =
    CUSTOM_CATEGORIES.find((c) => c.id === formData.category) ||
    CUSTOM_CATEGORIES[0];

  return (
    <>
      <Header />

      {/* HERO SECTION */}
      <section className="pod-hero">
        <div className="pod-hero-overlay"></div>
        <div className="pod-hero-container">
          <div className="pod-hero-content">
            <span className="pod-badge">
              <GiSparkles className="pod-badge-icon" /> Made To Order • Consecrated For You
            </span>

            <h1 className="pod-hero-title">
              Product On <span>Demand</span>
            </h1>

            <p className="pod-hero-subtitle">
              Can't find the exact spiritual item, specific mukhi combination, or custom gemstone ring size?
              We craft bespoke Vedic artifacts, custom energy items, and personalized puja samagri tailored to your birth chart and intentions.
            </p>

            <div className="pod-hero-trust-row">
              <div className="pod-trust-tag">
                <FaCertificate /> 100% Lab Certified
              </div>
              <div className="pod-trust-tag">
                <FaShieldAlt /> Vedic Consecration
              </div>
              <div className="pod-trust-tag">
                <GiMeditation /> Astrologer Guided
              </div>
            </div>

            <div className="pod-hero-cta">
              <a href="#custom-request-form" className="pod-btn-primary">
                Request Custom Quote <FiArrowRight />
              </a>
              <a href="#custom-offerings" className="pod-btn-secondary">
                Explore Categories
              </a>
            </div>
          </div>

          <div className="pod-hero-card">
            <div className="pod-card-badge">Bespoke Excellence</div>
            <img
              src="https://i.pinimg.com/1200x/e1/2f/c1/e12fc192c5177766be8b5d71797dd1ab.jpg"
              alt="Product On Demand"
              className="pod-card-img"
            />
            <div className="pod-card-info">
              <h4>Handcrafted & Consecrated</h4>
              <p>Personalized according to your Name, Nakshatra & Gotra</p>
            </div>
          </div>
        </div>
      </section>

      {/* BREADCRUMB */}
      <div className="pod-breadcrumb-container">
        <div className="pod-breadcrumb">
          <Link to="/">Home</Link>
          <span>/</span>
          <Link to="/shop/rudrakshas">The Shoppe</Link>
          <span>/</span>
          <span className="current">Product On Demand</span>
        </div>
      </div>

      {/* WHY CHOOSE PRODUCT ON DEMAND */}
      <section className="pod-features-section">
        <div className="pod-section-header">
          <span className="pod-subheading">Uncompromised Quality</span>
          <h2>
            Why Choose Our Custom Services
            <div className="pod-heading-divider">
              <img
                src="https://res.cloudinary.com/dbb2hkjri/image/upload/v1786441287/1684481897-flower-divider-horizontal-orange_f2omyx.svg"
                alt="Divider"
              />
            </div>
          </h2>
        </div>

        <div className="pod-features-grid">
          <div className="pod-feature-card">
            <div className="pod-feature-icon-box">
              <GiSparkles />
            </div>
            <h3>100% Tailored Specifications</h3>
            <p>
              Choose your exact metal (22K Gold, Silver, Panchdhatu), bead sizes, mukhis, gemstone carats, or dimensional requirements.
            </p>
          </div>

          <div className="pod-feature-card">
            <div className="pod-feature-icon-box">
              <GiMeditation />
            </div>
            <h3>Name & Gotra Consecration</h3>
            <p>
              Every custom order undergoes authentic Vedic Pran-Pratishtha Puja chanting your personal Name, Birth Star, and Gotra before shipping.
            </p>
          </div>

          <div className="pod-feature-card">
            <div className="pod-feature-icon-box">
              <FaCertificate />
            </div>
            <h3>Government Lab Certified</h3>
            <p>
              Accompanied by an official ISO-certified gemstone and spiritual laboratory certificate verifying 100% natural origin and authenticity.
            </p>
          </div>

          <div className="pod-feature-card">
            <div className="pod-feature-icon-box">
              <FiHeadphones />
            </div>
            <h3>Free Astrologer Consultation</h3>
            <p>
              Unsure which combination or gemstone suits you best? Our senior Vedic consultants will review your birth details free of charge.
            </p>
          </div>
        </div>
      </section>

      {/* CUSTOM OFFERINGS CATEGORIES GRID */}
      <section className="pod-offerings-section" id="custom-offerings">
        <div className="pod-section-header">
          <span className="pod-subheading">Crafted To Perfection</span>
          <h2>
            Customizable Categories
            <div className="pod-heading-divider">
              <img
                src="https://res.cloudinary.com/dbb2hkjri/image/upload/v1786441287/1684481897-flower-divider-horizontal-orange_f2omyx.svg"
                alt="Divider"
              />
            </div>
          </h2>
          <p className="pod-section-desc">
            Select a category below to pre-fill your custom order request form.
          </p>
        </div>

        <div className="pod-offerings-grid">
          {CUSTOM_CATEGORIES.map((item) => (
            <div
              key={item.id}
              className={`pod-offering-card ${
                formData.category === item.id ? "active-offering" : ""
              }`}
            >
              <div className="pod-offering-image-wrap">
                <img src={item.image} alt={item.title} />
                <div className="pod-offering-icon">{item.icon}</div>
              </div>
              <div className="pod-offering-body">
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <div className="pod-offering-tags">
                  {item.tags.map((tag, idx) => (
                    <span key={idx} className="pod-tag">
                      {tag}
                    </span>
                  ))}
                </div>
                <button
                  type="button"
                  className="pod-customize-btn"
                  onClick={() => handleSelectCategory(item.id)}
                >
                  Customize This Item <FiArrowRight />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FORM & SUMMARY SECTION */}
      <section className="pod-form-section" id="custom-request-form">
        <div className="pod-form-container">
          <div className="pod-form-header">
            <span className="pod-subheading">Get A Custom Quote</span>
            <h2>Submit Your Custom Request</h2>
            <p>
              Fill out your details below and upload any reference image or design. Our Vedic Specialists will review your specifications and contact you via Phone/WhatsApp within 2 to 4 hours.
            </p>
          </div>

          {errorMsg && <div className="pod-form-error">{errorMsg}</div>}

          {status === "success" ? (
            <div className="pod-success-card">
              <div className="pod-success-icon">
                <FaCheckCircle />
              </div>
              <h3>Custom Request Received!</h3>
              <p className="pod-ref-number">
                Request Reference ID: <strong>{requestRef}</strong>
              </p>
              <p className="pod-success-text">
                Thank you <strong>{formData.fullName}</strong>! We have received your specifications for{" "}
                <strong>{selectedCategoryObj.title}</strong>. Our Vedic scholar will review your chart & requirements and get in touch with you at <strong>{formData.phone}</strong> shortly.
              </p>
              {imagePreview && (
                <div className="pod-success-image">
                  <span>Attached Reference Image:</span>
                  <img src={imagePreview} alt="Attached Reference" />
                </div>
              )}
              <div className="pod-success-actions">
                <button
                  type="button"
                  className="pod-btn-primary"
                  onClick={() => {
                    setStatus("idle");
                    setImageFile(null);
                    setImagePreview(null);
                    setFormData({
                      fullName: "",
                      phone: "",
                      email: "",
                      category: "rudraksha",
                      metalPreference: "Panchdhatu",
                      dob: "",
                      rashi: "",
                      budget: "5000-15000",
                      description: "",
                    });
                  }}
                >
                  Submit Another Request
                </button>

                <a
                  href={`https://wa.me/917351519573?text=Hi%2C%20I%20have%20submitted%20a%20Product%20On%20Demand%20request%20ref%3A%20${requestRef}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pod-btn-whatsapp"
                >
                  <FaWhatsapp /> Chat On WhatsApp Now
                </a>
              </div>
            </div>
          ) : (
            <div className="pod-form-layout">
              {/* Form Left */}
              <form onSubmit={handleSubmit} className="pod-main-form">
                <div className="pod-form-grid">
                  <div className="pod-form-group">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      name="fullName"
                      placeholder="e.g. Rahul Sharma"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="pod-form-group">
                    <label>Phone / WhatsApp Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="e.g. +91 9876543210"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="pod-form-group">
                    <label>Email Address</label>
                    <input
                      type="email"
                      name="email"
                      placeholder="e.g. rahul@example.com"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="pod-form-group">
                    <label>Product Category *</label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                    >
                      {CUSTOM_CATEGORIES.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="pod-form-group">
                    <label>Preferred Metal / String Material</label>
                    <select
                      name="metalPreference"
                      value={formData.metalPreference}
                      onChange={handleInputChange}
                    >
                      <option value="Panchdhatu">Panchdhatu (5 Sacred Metals)</option>
                      <option value="Ashtadhatu">Ashtadhatu (8 Sacred Metals)</option>
                      <option value="Pure Silver (925)">Pure 925 Sterling Silver</option>
                      <option value="22K Gold">22K Hallmark Gold</option>
                      <option value="Brass / Copper">Pure Brass / Copper</option>
                      <option value="Sacred Thread / Silk">Sacred Silk / Wool Thread</option>
                      <option value="Not Applicable">Not Applicable</option>
                    </select>
                  </div>

                  <div className="pod-form-group">
                    <label>Budget Range (INR)</label>
                    <select
                      name="budget"
                      value={formData.budget}
                      onChange={handleInputChange}
                    >
                      <option value="1000-5000">₹1,000 - ₹5,000</option>
                      <option value="5000-15000">₹5,000 - ₹15,000</option>
                      <option value="15000-50000">₹15,000 - ₹50,000</option>
                      <option value="50000+">₹50,000+ (Premium / Rare)</option>
                    </select>
                  </div>

                  <div className="pod-form-group">
                    <label>Date of Birth (Optional for Kundli check)</label>
                    <input
                      type="date"
                      name="dob"
                      value={formData.dob}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="pod-form-group">
                    <label>Zodiac / Rashi (Optional)</label>
                    <input
                      type="text"
                      name="rashi"
                      placeholder="e.g. Mesh / Aries"
                      value={formData.rashi}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="pod-form-group full-width">
                  <label>Describe Your Custom Requirements</label>
                  <textarea
                    name="description"
                    rows="4"
                    placeholder="Tell us about specific mukhi counts, gemstone weight (in Ratti/Carat), ring size, dimensions, intended purpose (e.g. career growth, health, protection), or any custom design details..."
                    value={formData.description}
                    onChange={handleInputChange}
                  ></textarea>
                </div>

                {/* NEW FIELD FOR IMAGE UPLOAD */}
                <div className="pod-form-group full-width">
                  <label>
                    <FaImage className="gold-icon" /> Upload Reference Image / Design Sketch (Optional)
                  </label>
                  <div className="pod-file-upload-box">
                    {!imagePreview ? (
                      <label className="pod-file-dropzone">
                        <FiUploadCloud className="pod-drop-icon" />
                        <span>Click or Drag & Drop image to upload</span>
                        <small>Supports PNG, JPG, JPEG, WEBP (Max 10MB)</small>
                        <input
                          type="file"
                          accept="image/png, image/jpeg, image/jpg, image/webp"
                          onChange={handleImageChange}
                          style={{ display: "none" }}
                        />
                      </label>
                    ) : (
                      <div className="pod-image-preview-card">
                        <img src={imagePreview} alt="Uploaded Reference" />
                        <div className="pod-image-preview-details">
                          <span className="pod-image-name">{imageFile?.name}</span>
                          <span className="pod-image-size">
                            {(imageFile?.size / 1024 / 1024).toFixed(2)} MB
                          </span>
                        </div>
                        <button
                          type="button"
                          className="pod-remove-img-btn"
                          onClick={handleRemoveImage}
                          title="Remove image"
                        >
                          <FaTrash /> Remove
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  className="pod-submit-btn"
                  disabled={status === "submitting"}
                >
                  {status === "submitting" ? (
                    "Submitting Your Request..."
                  ) : (
                    <>
                      Submit Custom Request <FiSend />
                    </>
                  )}
                </button>
              </form>

              {/* Form Summary Side Card */}
              <div className="pod-summary-sidebar">
                <div className="pod-summary-card">
                  <h3>Your Selected Specification</h3>
                  <div className="pod-summary-image">
                    <img
                      src={imagePreview || selectedCategoryObj.image}
                      alt={selectedCategoryObj.title}
                    />
                  </div>
                  <div className="pod-summary-details">
                    <div className="pod-summary-row">
                      <span>Category:</span>
                      <strong>{selectedCategoryObj.title}</strong>
                    </div>
                    <div className="pod-summary-row">
                      <span>Preferred Metal:</span>
                      <strong>{formData.metalPreference}</strong>
                    </div>
                    <div className="pod-summary-row">
                      <span>Est. Budget Range:</span>
                      <strong>₹{formData.budget}</strong>
                    </div>
                    <div className="pod-summary-row">
                      <span>Reference Photo:</span>
                      <strong>{imageFile ? "Attached ✓" : "None"}</strong>
                    </div>
                  </div>
                  <div className="pod-guarantee-box">
                    <p>
                      <FaShieldAlt className="gold-icon" /> <strong>Authenticity Pledge:</strong> All items are certified natural & consecrated with authentic Vedic Mantras.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* HOW IT WORKS PROCESS */}
      <section className="pod-process-section">
        <div className="pod-section-header">
          <span className="pod-subheading">Transparent Step-By-Step</span>
          <h2>
            How Product On Demand Works
            <div className="pod-heading-divider">
              <img
                src="https://res.cloudinary.com/dbb2hkjri/image/upload/v1786441287/1684481897-flower-divider-horizontal-orange_f2omyx.svg"
                alt="Divider"
              />
            </div>
          </h2>
        </div>

        <div className="pod-process-grid">
          <div className="pod-process-step">
            <div className="pod-step-num">01</div>
            <h3>Submit Specifications</h3>
            <p>
              Fill out the custom request form with your required items, metal choices, optional reference image, or birth details.
            </p>
          </div>

          <div className="pod-process-step">
            <div className="pod-step-num">02</div>
            <h3>Consultation & Quote</h3>
            <p>
              Our astrologer and master artisan review your chart and provide exact specifications & transparent pricing.
            </p>
          </div>

          <div className="pod-process-step">
            <div className="pod-step-num">03</div>
            <h3>Handcrafting & Ritual</h3>
            <p>
              Items are handcrafted with high precision and energized in your Name and Gotra in sacred Vedic Pujas.
            </p>
          </div>

          <div className="pod-process-step">
            <div className="pod-step-num">04</div>
            <h3>Lab Certified Delivery</h3>
            <p>
              Dispatched with an ISO laboratory certificate and detailed wearing/worship instructions to your doorstep.
            </p>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="pod-reviews-section">
        <div className="pod-section-header">
          <span className="pod-subheading">Real Customer Experiences</span>
          <h2>
            What Buyers Say About Custom Orders
            <div className="pod-heading-divider">
              <img
                src="https://res.cloudinary.com/dbb2hkjri/image/upload/v1786441287/1684481897-flower-divider-horizontal-orange_f2omyx.svg"
                alt="Divider"
              />
            </div>
          </h2>
        </div>

        <div className="pod-reviews-grid">
          {TESTIMONIALS.map((rev, i) => (
            <div key={i} className="pod-review-card">
              <div className="pod-review-stars">
                {[...Array(rev.rating)].map((_, idx) => (
                  <FaStar key={idx} />
                ))}
              </div>
              <p className="pod-review-comment">"{rev.comment}"</p>
              <div className="pod-review-author">
                <h4>{rev.name}</h4>
                <span>
                  {rev.location} • <em>{rev.product}</em>
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="pod-faq-section">
        <div className="pod-section-header">
          <span className="pod-subheading">Have Questions?</span>
          <h2>
            Frequently Asked Questions
            <div className="pod-heading-divider">
              <img
                src="https://res.cloudinary.com/dbb2hkjri/image/upload/v1786441287/1684481897-flower-divider-horizontal-orange_f2omyx.svg"
                alt="Divider"
              />
            </div>
          </h2>
        </div>

        <div className="pod-faq-container">
          {FAQS.map((faq, index) => (
            <div
              key={index}
              className={`pod-faq-card ${openFaqIndex === index ? "active" : ""}`}
            >
              <button
                type="button"
                className="pod-faq-btn"
                onClick={() => toggleFaq(index)}
              >
                <span>{faq.question}</span>
                {openFaqIndex === index ? <FaChevronUp /> : <FaChevronDown />}
              </button>
              {openFaqIndex === index && (
                <div className="pod-faq-answer">
                  <p>{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* SUPPORT & WHATSAPP BANNER */}
      <section className="support-section">
        <div className="support-content">
          <h2>Need Instant Guidance On Your Custom Order?</h2>
          <p>
            Connect directly with our Vedic experts on Phone or WhatsApp for immediate consultation and custom orders.
          </p>
          <div className="pod-support-buttons">
            <a href="tel:7351519573" className="support-btn">
              <FaPhoneAlt /> Call Us: 7351519573
            </a>
            <a
              href="https://wa.me/917351519573?text=Hi%2C%20I%20want%20to%20inquire%20about%20a%20Product%20On%20Demand%20custom%20order."
              target="_blank"
              rel="noopener noreferrer"
              className="support-btn whatsapp-btn"
            >
              <FaWhatsapp /> Chat On WhatsApp
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
